import { useState } from 'react';
import { Text, Button, Checkbox, Spinner } from '@belong/ui';
import {
  TourAttendeeDetailModel,
  UnitListingDetailsModel,
} from 'src/api/models';
import { HOUSE_PAGE_STRINGS } from 'src/features/house-page/house-page.strings';
import {
  useCancelTour,
  useUpdateHomeInterest,
} from 'src/features/house-page/queries';
import { TourModalStep } from '../tour-modal';

type TourCancelProps = {
  // Optional: `TourModal` spreads one `commonProps` into every step, and its
  // `bookedTour` is undefined until the booked-tours query resolves — which is
  // exactly the case this component already returns `null` for.
  bookedTour?: TourAttendeeDetailModel;
  home: UnitListingDetailsModel;
  onDismiss: () => void;
  setStep: (step: TourModalStep) => void;
};

export function TourCancel({
  bookedTour,
  home,
  onDismiss,
  setStep,
}: TourCancelProps) {
  const [noLongerInterested, setNoLongerInterested] = useState(false);
  const { mutateCancelTour, isCancelMutating } = useCancelTour({
    tourId: bookedTour?.tour?.uniqueId!,
    homeId: home.basicInfo!.unitId!,
    isPrivateTour: bookedTour?.tour?.type === 'PrivateTour',
  });
  const { mutateUpdateHomeInterest, isHomeInterestMutating } =
    useUpdateHomeInterest({
      homeId: home.basicInfo!.unitId!,
      data: {
        noLongerInMarket: true,
        interestLevel: 'NotInterested',
      },
    });

  if (!bookedTour || !bookedTour.tour) {
    return null;
  }

  async function handleCancel() {
    if (noLongerInterested) {
      await mutateUpdateHomeInterest();
    }

    await mutateCancelTour();

    onDismiss();
  }

  const { type: tourType } = bookedTour.tour;
  return (
    <>
      {(isCancelMutating || isHomeInterestMutating) && <Spinner />}
      <div className="px-sm w-full">
        <div className="gap-xs flex flex-col items-center text-center ">
          <Text fontWeight="semibold" variant="h2">
            {HOUSE_PAGE_STRINGS['tour.cancel.areYouSure']}
          </Text>
        </div>
        <div className="mt-xl flex justify-center">
          <Checkbox
            checked={noLongerInterested}
            label={HOUSE_PAGE_STRINGS['tour.cancel.noLongerInterested']}
            id={'no-longer-interested'}
            onChange={() => setNoLongerInterested((val) => !val)}
          />
        </div>
      </div>
      <div className="mt-sm gap-xl px-2sm md:mt-2xl md:border-dark-gray md:bg-light-gray md:p-md flex w-full flex-col-reverse items-center md:flex-row md:justify-between md:border-0 md:border-t md:border-solid">
        <div>
          {tourType !== 'PrivateTour' && (
            <Button
              variant="text"
              onClick={() => setStep(TourModalStep.TourSelection)}
            >
              {HOUSE_PAGE_STRINGS['tour.rescheduleInstead']}
            </Button>
          )}
        </div>
        <Button
          buttonType="danger"
          size={{ _: 'fluid', lg: 'default' }}
          onClick={handleCancel}
        >
          {HOUSE_PAGE_STRINGS['tour.cancelTour']}
        </Button>
      </div>
    </>
  );
}
